import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FormCard } from "Components/Form/FormCard";
import { I18N_NS } from "Config/i18n";
import { API } from "General/API";
import { API_URL } from "General/APIEndpoints";
import { Button, Col, Form, InputNumber, Popconfirm, Row, Select, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { notificationSuccess } from "utils/libHelpers/AntdNotification";
import { McpAdminFeedback } from "./McpAdminFeedback";

const emptyWindows = (): API_McpQuotaWindows => ({ minute: 0, day: 0, month: 0 });

export const McpQuotaTab = () => {
  const { t } = useTranslation(I18N_NS.ADMIN);
  const [loading, setLoading] = useState(false);
  const [defaultsForm] = Form.useForm<API_McpQuotaMeters>();
  const [overrideForm] = Form.useForm<API_McpQuotaOverride>();
  const [overrides, setOverrides] = useState<API_McpQuotaOverride[]>([]);
  const [feedback, setFeedback] = useState<string | null>(null);

  const showOk = (key: string) => {
    const msg = t(key);
    notificationSuccess(t("Success"), msg);
    setFeedback(msg);
  };

  const fetchPolicy = async () => {
    setLoading(true);
    try {
      const policy = await API.getData<API_McpQuotaPolicy>(API_URL.MCP_ADMIN.getQuotaPolicy());
      defaultsForm.setFieldsValue({
        toolCalls: policy.defaults?.toolCalls ?? emptyWindows(),
        paveCalls: policy.defaults?.paveCalls ?? emptyWindows(),
        bytes: policy.defaults?.bytes ?? emptyWindows(),
      });
      setOverrides(policy.overrides ?? []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPolicy();
  }, []);

  const saveDefaults = async (values: API_McpQuotaMeters) => {
    await API.editData(API_URL.MCP_ADMIN.updateQuotaDefaults(), values);
    await fetchPolicy();
    showOk("MCP quota defaults saved");
  };

  const saveOverride = async (values: API_McpQuotaOverride) => {
    await API.newData(API_URL.MCP_ADMIN.upsertQuotaOverride(), values);
    overrideForm.resetFields();
    await fetchPolicy();
    showOk("MCP quota override saved");
  };

  const removeOverride = async (id: number) => {
    await API.deleteData(API_URL.MCP_ADMIN.deleteQuotaOverride(id));
    await fetchPolicy();
    showOk("MCP quota override deleted");
  };

  const columns: ColumnsType<API_McpQuotaOverride> = [
    { dataIndex: "id", title: "ID", width: 80 },
    { dataIndex: "scope", title: t("Scope"), width: 140 },
    { dataIndex: "userId", title: t("User ID"), width: 100 },
    { dataIndex: "projectId", title: t("Project ID"), width: 110 },
    { dataIndex: "meter", title: t("Meter"), width: 140 },
    { dataIndex: "window", title: t("Window"), width: 110 },
    { dataIndex: "limit", title: t("Limit"), width: 140 },
    {
      title: t("Actions"),
      width: 80,
      render: (_v, record) =>
        record.id != null ? (
          <Popconfirm title={t("Delete override?")} onConfirm={() => removeOverride(record.id)}>
            <Button type="link" size="small" aria-label={t("Delete quota override")}>
              <FontAwesomeIcon icon={["far", "trash-alt"]} />
            </Button>
          </Popconfirm>
        ) : null,
    },
  ];

  const windowFields = (meter: "toolCalls" | "paveCalls" | "bytes", label: string) => (
    <Col xs={24} md={8}>
      <h3>{t(label)}</h3>
      <Form.Item name={[meter, "minute"]} label={t("Minute")} rules={[{ required: true }]}>
        <InputNumber min={0} style={{ width: "100%" }} />
      </Form.Item>
      <Form.Item name={[meter, "day"]} label={t("Day")} rules={[{ required: true }]}>
        <InputNumber min={0} style={{ width: "100%" }} />
      </Form.Item>
      <Form.Item name={[meter, "month"]} label={t("Month")} rules={[{ required: true }]}>
        <InputNumber min={0} style={{ width: "100%" }} />
      </Form.Item>
    </Col>
  );

  return (
    <div>
      <FormCard title={t("MCP Quota")} loading={loading} className="mb-2">
        <McpAdminFeedback message={feedback} />
        <p>
          {t(
            "First applicable limit wins: user+project, then user, then project, then default. " +
              "MCP picks up changes within about 15 seconds without redeploy."
          )}
        </p>
        <Form form={defaultsForm} layout="vertical" onFinish={saveDefaults}>
          <Row gutter={16}>
            {windowFields("toolCalls", "Tool calls")}
            {windowFields("paveCalls", "PAVE API calls")}
            {windowFields("bytes", "Bytes transferred")}
          </Row>
          <Button type="primary" htmlType="submit">
            {t("Save defaults")}
          </Button>
        </Form>
      </FormCard>
      <FormCard title={t("Quota overrides")} loading={loading}>
        <Form form={overrideForm} layout="vertical" onFinish={saveOverride}>
          <Row gutter={16}>
            <Col xs={24} md={6}>
              <Form.Item name="scope" label={t("Scope")} rules={[{ required: true }]} initialValue="user">
                <Select
                  options={[
                    { value: "user", label: t("User") },
                    { value: "project", label: t("Project") },
                    { value: "user_project", label: t("User and project") },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={12} md={4}>
              <Form.Item name="userId" label={t("User ID")}>
                <InputNumber min={1} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={12} md={4}>
              <Form.Item name="projectId" label={t("Project ID")}>
                <InputNumber min={1} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={12} md={4}>
              <Form.Item name="meter" label={t("Meter")} rules={[{ required: true }]} initialValue="tool_calls">
                <Select
                  options={[
                    { value: "tool_calls", label: t("Tool calls") },
                    { value: "pave_calls", label: t("PAVE API calls") },
                    { value: "bytes", label: t("Bytes transferred") },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={12} md={3}>
              <Form.Item name="window" label={t("Window")} rules={[{ required: true }]} initialValue="minute">
                <Select
                  options={[
                    { value: "minute", label: t("Minute") },
                    { value: "day", label: t("Day") },
                    { value: "month", label: t("Month") },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={12} md={3}>
              <Form.Item name="limit" label={t("Limit")} rules={[{ required: true }]}>
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
          <Button type="primary" htmlType="submit">
            {t("Add or update override")}
          </Button>
        </Form>
        <Table
          className="mt-2"
          rowKey={(row) => String(row.id ?? `${row.scope}-${row.meter}-${row.window}`)}
          columns={columns}
          dataSource={overrides}
          pagination={false}
          size="small"
        />
      </FormCard>
    </div>
  );
};
